import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { Timer, TimerRecurrence, TimerRecurrenceUnit } from './TimerCard';
import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface TimerTagOption {
  id: number;
  name: string;
  usage_count: number;
}

export interface TimerFormData {
  title: string;
  description: string | null;
  target_date: string;
  timer_type: 'count_up' | 'countdown';
  is_recurring: boolean;
  recurrence_type: TimerRecurrence;
  recurrence_interval: number;
  recurrence_unit: TimerRecurrenceUnit | null;
  recurrence_anchor_date: string | null;
  tags: string[];
  color: string;
  is_active: boolean;
}

interface TimerDraftData {
  title: string;
  description: string;
  target_date: string;
  timer_type: 'count_up' | 'countdown';
  recurrence_type: TimerRecurrence;
  recurrence_interval: number;
  recurrence_unit: TimerRecurrenceUnit;
  recurrence_anchor_date: string;
  tags: string[];
  color: string;
  is_active: boolean;
}

interface AddTimerDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (timer: TimerFormData) => Promise<void>;
  editTimer?: Timer | null;
}

const colorOptions = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#F97316', label: 'Orange' },
];

const recurrenceOptions: Array<{ value: TimerRecurrence; label: string }> = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom interval' },
  { value: 'yearly', label: 'Yearly' },
];

const unitOptions: Array<{ value: TimerRecurrenceUnit; label: string }> = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

// A `datetime-local` input both displays and returns LOCAL wall clock, and the
// save path parses that bare string back as local (`new Date(...)` in
// Timers.tsx). Emitting a UTC wall clock here breaks that symmetry: the input
// shows the wrong time for any non-UTC user, and re-saving without editing
// shifts the stored instant by the UTC offset *every* time, compounding
// without bound. Build the string from local components so the round trip is
// lossless.
const formatDateForInput = (dateString?: string | null): string => {
  const date = dateString ? new Date(dateString) : new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const normalizeTags = (values: string[]): string[] => {
  const seen = new Set<string>();
  const tags: string[] = [];
  values.forEach((raw) => {
    const tag = raw.trim().toLowerCase();
    if (tag && !seen.has(tag)) {
      seen.add(tag);
      tags.push(tag);
    }
  });
  return tags;
};

const defaultDraft = (): TimerDraftData => ({
  title: '',
  description: '',
  target_date: formatDateForInput(),
  timer_type: 'countdown',
  recurrence_type: 'none',
  recurrence_interval: 1,
  recurrence_unit: 'hours',
  recurrence_anchor_date: '',
  tags: [],
  color: '#3B82F6',
  is_active: true,
});

const recurrenceUnitLabel = (recurrenceType: TimerRecurrence) => {
  if (recurrenceType === 'daily') return 'days';
  if (recurrenceType === 'weekly') return 'weeks';
  if (recurrenceType === 'monthly') return 'months';
  if (recurrenceType === 'yearly') return 'years';
  return 'intervals';
};

const AddTimerDialog: React.FC<AddTimerDialogProps> = ({
  open,
  onClose,
  onSave,
  editTimer,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [formData, setFormData] = useState<TimerDraftData>(defaultDraft());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagOptions, setTagOptions] = useState<string[]>([]);

  // Suggest the user's existing tags (from the tag-management API) so a saved
  // timer reuses a tag instead of drifting into near-duplicates. A failed
  // request degrades to no suggestions — freeSolo entry still works.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    axios
      .get<TimerTagOption[]>(`${API_BASE_URL}/api/timer-tags/`)
      .then((response) => {
        if (!cancelled) setTagOptions(response.data.map((tag) => tag.name));
      })
      .catch(() => {
        if (!cancelled) setTagOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (editTimer) {
      const recurrenceType = editTimer.recurrence_type || 'none';
      setFormData({
        title: editTimer.title,
        description: editTimer.description || '',
        target_date: formatDateForInput(editTimer.target_date),
        timer_type: editTimer.timer_type,
        recurrence_type: recurrenceType,
        recurrence_interval: editTimer.recurrence_interval || 1,
        recurrence_unit: editTimer.recurrence_unit || 'hours',
        recurrence_anchor_date: editTimer.recurrence_anchor_date
          ? formatDateForInput(editTimer.recurrence_anchor_date)
          : '',
        tags: [...(editTimer.tags || [])],
        color: editTimer.color,
        is_active: editTimer.is_active,
      });
    } else {
      setFormData(defaultDraft());
    }
    setError(null);
  }, [editTimer, open]);

  const handleChange = (field: keyof TimerDraftData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'recurrence_type' && value === 'none') {
        next.recurrence_interval = 1;
        next.recurrence_anchor_date = '';
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.target_date) {
      setError('Target date is required');
      return;
    }
    if (formData.recurrence_interval < 1) {
      setError('Recurrence interval must be at least 1');
      return;
    }

    setLoading(true);
    setError(null);

    // Recurrence only applies to countdowns: the backend ignores it for
    // count-up timers, so force 'none' rather than persisting dead config.
    const recurrenceType: TimerRecurrence =
      formData.timer_type === 'count_up' ? 'none' : formData.recurrence_type;
    const isRecurring = recurrenceType !== 'none';
    const payload: TimerFormData = {
      title: formData.title.trim(),
      description: formData.description.trim() || null,
      target_date: formData.target_date,
      timer_type: formData.timer_type,
      is_recurring: isRecurring,
      recurrence_type: recurrenceType,
      recurrence_interval: isRecurring ? Number(formData.recurrence_interval) : 1,
      recurrence_unit: isRecurring && formData.recurrence_type === 'custom'
        ? formData.recurrence_unit
        : null,
      recurrence_anchor_date: isRecurring && formData.recurrence_anchor_date
        ? formData.recurrence_anchor_date
        : null,
      tags: normalizeTags(formData.tags),
      color: formData.color,
      is_active: formData.is_active,
    };

    try {
      await onSave(payload);
      onClose();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save timer'));
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!editTimer;
  const isCountUp = formData.timer_type === 'count_up';
  const isRecurring = !isCountUp && formData.recurrence_type !== 'none';
  const isCustom = formData.recurrence_type === 'custom';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Timer' : 'Add New Timer'}</DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Title"
          fullWidth
          required
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Days until launch"
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          sx={{ mb: 2 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="timer-type-label">Timer Type</InputLabel>
              <Select
                labelId="timer-type-label"
                value={formData.timer_type}
                label="Timer Type"
                onChange={(e) => handleChange('timer_type', e.target.value)}
              >
                <MenuItem value="countdown">Countdown</MenuItem>
                <MenuItem value="count_up">Count Up</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label={formData.timer_type === 'countdown' ? 'Target Date' : 'Start Date'}
              type="datetime-local"
              fullWidth
              required
              value={formData.target_date}
              onChange={(e) => handleChange('target_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 0, mb: 2 }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Tooltip
              title={isCountUp ? 'Recurrence applies to countdown timers only' : ''}
            >
              <FormControl fullWidth sx={{ mb: 2 }} disabled={isCountUp}>
                <InputLabel id="timer-recurrence-label">Recurrence</InputLabel>
                <Select
                  labelId="timer-recurrence-label"
                  value={isCountUp ? 'none' : formData.recurrence_type}
                  label="Recurrence"
                  onChange={(e) => handleChange('recurrence_type', e.target.value)}
                >
                  {recurrenceOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          </Grid>
          {isRecurring && !isCustom && (
            <Grid item xs={12} sm={6}>
              <TextField
                label={`Every N ${recurrenceUnitLabel(formData.recurrence_type)}`}
                type="number"
                fullWidth
                value={formData.recurrence_interval}
                onChange={(e) => handleChange('recurrence_interval', Number(e.target.value))}
                inputProps={{ min: 1 }}
                sx={{ mb: 2 }}
              />
            </Grid>
          )}
          {isRecurring && isCustom && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Every"
                  type="number"
                  fullWidth
                  value={formData.recurrence_interval}
                  onChange={(e) => handleChange('recurrence_interval', Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={formData.recurrence_unit}
                    label="Unit"
                    onChange={(e) => handleChange('recurrence_unit', e.target.value)}
                  >
                    {unitOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {isRecurring && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Anchor Date"
                type="datetime-local"
                fullWidth
                value={formData.recurrence_anchor_date}
                onChange={(e) => handleChange('recurrence_anchor_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </Grid>
          )}
        </Grid>

        <Autocomplete
          multiple
          freeSolo
          options={tagOptions}
          value={formData.tags}
          onChange={(_, value) =>
            handleChange(
              'tags',
              value.map((tag) => tag.trim().toLowerCase()).filter(Boolean),
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label="Tags"
              placeholder="p0, deep-work, writing"
            />
          )}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel>Color</InputLabel>
          <Select
            value={formData.color}
            label="Color"
            onChange={(e) => handleChange('color', e.target.value)}
            renderValue={(value) => (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    bgcolor: value,
                  }}
                />
                <Typography variant="body2">
                  {colorOptions.find(c => c.value === value)?.label || 'Custom'}
                </Typography>
              </Stack>
            )}
          >
            {colorOptions.map((color) => (
              <MenuItem key={color.value} value={color.value}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: 1,
                      bgcolor: color.value,
                    }}
                  />
                  <Typography variant="body2">{color.label}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {isEditing ? 'Save Changes' : 'Add Timer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTimerDialog;
